import React, { useState, useEffect, useMemo } from 'react';
import { FaEye, FaSearch, FaDownload, FaComments, FaUser, FaClock, FaCheckCircle, FaSpinner, FaExclamationTriangle, FaEdit, FaUserPlus, FaTimes } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { ticketService } from '../../../../services/ticketService';

const Support = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [ticketStats, setTicketStats] = useState({
    total: 0,
    inProgress: 0,
    highPriority: 0
  });
  const [agents, setAgents] = useState([]);

  // Load tickets from API on mount
  useEffect(() => {
    loadTickets();
    loadStats();
    // Auto-refresh every 30 seconds
    const interval = setInterval(() => {
      loadTickets();
      loadStats();
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  // Reload when filters change
  useEffect(() => {
    loadTickets();
  }, [statusFilter, priorityFilter, searchTerm]);

  const loadTickets = async () => {
    try {
      setLoading(true);
      const data = await ticketService.getAllTickets({
        status: statusFilter !== 'all' ? statusFilter : undefined,
        priority: priorityFilter !== 'all' ? priorityFilter : undefined,
        search: searchTerm || undefined
      });
      
      // Transform API data to match component format
      const transformedTickets = data.map(ticket => ({
        id: ticket._id || ticket.id,
        ticketId: ticket.ticketId,
        customer: ticket.userId?.name || 'Unknown',
        customerEmail: ticket.userId?.email || '',
        subject: ticket.subject,
        description: ticket.description,
        status: ticket.status,
        priority: ticket.priority,
        assignedTo: ticket.assignedTo?.name || 'Unassigned',
        assignedToId: ticket.assignedTo?._id || null,
        createdAt: ticket.createdAt ? new Date(ticket.createdAt).toLocaleDateString() : '',
        updatedAt: ticket.updatedAt ? new Date(ticket.updatedAt).toLocaleDateString() : '',
        category: ticket.category,
        orderId: ticket.orderId
      }));
      
      setTickets(transformedTickets);
    } catch (error) {
      console.error('Failed to load tickets:', error);
      toast.error(error.message || 'Failed to load tickets');
      setTickets([]);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const stats = await ticketService.getTicketStats();
      setTicketStats({
        total: stats.total || 0,
        inProgress: stats.inProgress || 0,
        highPriority: stats.highPriority || 0
      });
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const handleViewTicket = async (ticket) => {
    try {
      setLoading(true);
      const fullTicket = await ticketService.getTicketById(ticket.id);
      const history = await ticketService.getTicketHistory(ticket.id);
      const comments = await ticketService.getComments(ticket.id);
      
      setSelectedTicket({
        ...ticket,
        fullTicket,
        history,
        comments
      });
      setShowTicketModal(true);
    } catch (error) {
      toast.error(error.message || 'Failed to load ticket details');
    } finally {
      setLoading(false);
    }
  };

  const handleAssignTicket = async (ticketId, assignedToId) => {
    try {
      setLoading(true);
      await ticketService.assignTicket(ticketId, assignedToId);
      toast.success('Ticket assigned successfully');
      setShowAssignModal(false);
      await loadTickets();
      await loadStats();
    } catch (error) {
      toast.error(error.message || 'Failed to assign ticket');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (ticketId, status) => {
    try {
      setLoading(true);
      await ticketService.updateTicketStatus(ticketId, status);
      toast.success('Ticket status updated successfully');
      await loadTickets();
      await loadStats();
    } catch (error) {
      toast.error(error.message || 'Failed to update ticket status');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePriority = async (ticketId, priority) => {
    try {
      setLoading(true);
      await ticketService.updateTicketPriority(ticketId, priority);
      toast.success('Ticket priority updated successfully');
      await loadTickets();
      await loadStats();
    } catch (error) {
      toast.error(error.message || 'Failed to update ticket priority');
    } finally {
      setLoading(false);
    }
  };

  const filteredTickets = tickets;

  // Export tickets to CSV
  const handleExport = async () => {
    try {
      setExporting(true);
      
      const csvData = await ticketService.exportTickets({
        status: statusFilter !== 'all' ? statusFilter : undefined,
        priority: priorityFilter !== 'all' ? priorityFilter : undefined
      });
      
      // Create and download file
      const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `support_tickets_report_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success('Tickets exported successfully!');
    } catch (error) {
      console.error('Failed to export tickets:', error);
      toast.error(error.message || 'Failed to export tickets');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div>
          <h2 className="text-lg font-bold text-gray-900">Customer Support Management</h2>
          <p className="text-xs text-gray-600 mt-1">Respond to queries and manage support tickets</p>
        </div>
        <div className="flex gap-2">
          <button 
            onClick={handleExport}
            disabled={exporting || tickets.length === 0}
            className="bg-white border border-red-500 text-red-500 rounded-lg px-4 py-2 flex items-center gap-2 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-red-50 transition-colors"
            title="Export tickets to CSV"
          >
            {exporting ? <FaSpinner className="animate-spin" /> : <FaDownload className="text-red-500" />} <span className="text-red-500">Export</span>
          </button>
        </div>
      </div>

      {/* Summary Cards - Real-time Data */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-purple-50 rounded-lg p-6 border border-purple-200 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-purple-700 mb-1">Total Tickets</p>
              <p className="text-2xl font-bold text-purple-900">
                {ticketStats.total.toLocaleString()}
              </p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
              <FaUser className="text-xl text-purple-600" />
            </div>
          </div>
        </div>
        <div className="bg-yellow-50 rounded-lg p-6 border border-yellow-200 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-yellow-700 mb-1">In Progress</p>
              <p className="text-2xl font-bold text-yellow-900">
                {ticketStats.inProgress.toLocaleString()}
              </p>
            </div>
            <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
              <FaComments className="text-xl text-yellow-600" />
            </div>
          </div>
        </div>
        <div className="bg-red-50 rounded-lg p-6 border border-red-200 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-red-700 mb-1">High Priority</p>
              <p className="text-2xl font-bold text-red-900">
                {ticketStats.highPriority.toLocaleString()}
              </p>
            </div>
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
              <FaExclamationTriangle className="text-xl text-red-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-md p-4 border border-gray-100">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="relative">
            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search tickets..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Status</option>
            <option value="open">Open</option>
            <option value="in progress">In Progress</option>
            <option value="resolved">Resolved</option>
            <option value="closed">Closed</option>
          </select>
          <select
            value={priorityFilter}
            onChange={(e) => setPriorityFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Priority</option>
            <option value="high">High Priority</option>
            <option value="medium">Medium Priority</option>
            <option value="low">Low Priority</option>
          </select>
        </div>
      </div>

      {/* Tickets Table */}
      <div className="bg-white rounded-lg shadow-md border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="p-20 text-center">
            <FaSpinner className="animate-spin text-4xl text-blue-600 mx-auto mb-4" />
            <p className="text-gray-500 font-medium">Loading tickets...</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ticket ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Subject</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Priority</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Assigned To</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredTickets.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-8 text-center text-gray-500">
                      No tickets found matching your filters.
                    </td>
                  </tr>
                ) : (
                  filteredTickets.map((ticket) => (
                    <tr key={ticket.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm font-semibold text-gray-900">{ticket.ticketId}</td>
                      <td className="px-6 py-4 text-sm text-gray-600">{ticket.customer}</td>
                      <td className="px-6 py-4 text-sm text-gray-900">{ticket.subject}</td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          ticket.priority === 'High' ? 'bg-red-100 text-red-800' :
                          ticket.priority === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-green-100 text-green-800'
                        }`}>
                          {ticket.priority}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">{ticket.assignedTo}</td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          ticket.status === 'Resolved' ? 'bg-green-100 text-green-800' :
                          ticket.status === 'In Progress' ? 'bg-yellow-100 text-yellow-800' :
                          ticket.status === 'Closed' ? 'bg-gray-100 text-gray-800' :
                          'bg-blue-100 text-blue-800'
                        }`}>
                          {ticket.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-xs text-gray-500">{ticket.createdAt}</td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <button 
                            onClick={() => handleViewTicket(ticket)}
                            className="text-blue-600 hover:text-blue-900 p-2 hover:bg-blue-50 rounded-lg transition" 
                            title="View Details"
                          >
                            <FaEye />
                          </button>
                          {!ticket.assignedToId && (
                            <button 
                              onClick={() => {
                                setSelectedTicket(ticket);
                                setShowAssignModal(true);
                              }}
                              className="text-green-600 hover:text-green-900 p-2 hover:bg-green-50 rounded-lg transition" 
                              title="Assign Ticket"
                            >
                              <FaUserPlus />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Ticket Detail Modal */}
      {showTicketModal && selectedTicket && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 sticky top-0 bg-white">
              <div>
                <h3 className="text-2xl font-bold text-gray-900">{selectedTicket.ticketId}</h3>
                <p className="text-sm text-gray-600 mt-1">{selectedTicket.subject}</p>
              </div>
              <button
                onClick={() => {
                  setShowTicketModal(false);
                  setSelectedTicket(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <FaTimes className="text-xl" />
              </button>
            </div>
            <div className="p-6 space-y-6">
              {/* Ticket Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Customer</p>
                  <p className="font-semibold">{selectedTicket.customer}</p>
                  <p className="text-xs text-gray-500">{selectedTicket.customerEmail}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Status</p>
                  <select
                    value={selectedTicket.status}
                    onChange={(e) => handleUpdateStatus(selectedTicket.id, e.target.value)}
                    className="mt-1 px-3 py-1 border border-gray-300 rounded-lg text-sm"
                  >
                    <option value="Open">Open</option>
                    <option value="In Progress">In Progress</option>
                    <option value="Resolved">Resolved</option>
                    <option value="Closed">Closed</option>
                  </select>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Priority</p>
                  <select
                    value={selectedTicket.priority}
                    onChange={(e) => handleUpdatePriority(selectedTicket.id, e.target.value)}
                    className="mt-1 px-3 py-1 border border-gray-300 rounded-lg text-sm"
                  >
                    <option value="Low">Low</option>
                    <option value="Medium">Medium</option>
                    <option value="High">High</option>
                  </select>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Assigned To</p>
                  <p className="font-semibold">{selectedTicket.assignedTo}</p>
                </div>
              </div>

              {/* Description */}
              <div>
                <p className="text-sm font-semibold text-gray-700 mb-2">Description</p>
                <p className="text-sm text-gray-600 whitespace-pre-wrap">{selectedTicket.description}</p>
              </div>

              {/* History */}
              {selectedTicket.history && selectedTicket.history.length > 0 && (
                <div>
                  <p className="text-sm font-semibold text-gray-700 mb-3">Ticket History</p>
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {selectedTicket.history.map((entry, idx) => (
                      <div key={idx} className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="text-xs font-medium text-gray-900">{entry.action}</p>
                            <p className="text-xs text-gray-500">{entry.performedBy?.name || 'System'}</p>
                            <p className="text-xs text-gray-400">{new Date(entry.createdAt).toLocaleString()}</p>
                          </div>
                        </div>
                        {entry.comment && (
                          <p className="text-xs text-gray-600 mt-2">{entry.comment}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Support;
